<template>
  <div class="f360-input" :class="{ 'f360-input--full': full }">
    <div class="f360-input__info" v-if="label || tooltip">
      <p class="f360-input__label" v-if="label">{{ label }}</p>

      <template v-if="tooltip">
        <F360TooltipBase :tooltip="tooltip">
          <F360ButtonOutline @click="() => undefined" />
        </F360TooltipBase>
      </template>
    </div>

    <div
      class="f360-input__wrapper"
      :class="{
        'f360-input__wrapper--disabled': disabled,
        'f360-input__wrapper--error': error,
        'f360-input__wrapper--full': full
      }"
    >
      <MaterialIcon
        :icon="prefixIcon"
        class="f360-input__icon"
        v-if="prefixIcon"
      />
      <el-input
        v-bind="$attrs"
        :size="size"
        :disabled="disabled"
        :maxlength="maxLength"
        autocomplete="off"
      />
      <MaterialIcon icon="asterisk" class="f360-input__icon" v-if="error" />
      <MaterialIcon
        :icon="suffixIcon"
        class="f360-input__icon"
        v-if="suffixIcon"
      />
    </div>

    <span class="f360-input__error" v-if="showError && error">{{ error }}</span>
  </div>
</template>

<script setup lang="ts">
import F360ButtonOutline from "@/ui/components/Buttons/ButtonOutline/F360ButtonOutline.vue";
import F360TooltipBase from "@/ui/components/Tooltips/TooltipBase/F360TooltipBase.vue";
import MaterialIcon from "@/ui/components/Icons/MaterialIcon/MaterialIcon.vue";

const props = withDefaults(
  defineProps<{
    label?: string;
    size?: "default" | "small" | "large";
    error?: boolean | string;
    showError?: boolean;
    prefixIcon?: string;
    suffixIcon?: string;
    disabled?: boolean;
    full?: boolean;
    tooltip?: string;
    maxLength?: string;
  }>(),
  {
    size: "default"
  }
);

const options = {
  padding: {
    default: "8px",
    small: "4px",
    large: "12px"
  }
};

const padding = options.padding[props.size] || options.padding.default;

const height = props.size === "default" ? "32px" : "auto";
</script>

<style lang="scss">
@use "@/ui/styles/variables" as *;
@use "@/ui/styles/theme/f360-theme" as *;

.f360-input {
  &__info {
    width: fit-content;
    height: fit-content;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
  }

  &__label {
    margin: 0;
    font-family: $fontFamily;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0;
    color: $font-color;
    text-wrap: nowrap;
  }

  &__wrapper {
    box-sizing: border-box;
    padding: 0px v-bind(padding);
    background-color: #fff;
    border: 1px solid $input-border;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v-bind(padding);

    .financas-input {
      &__wrapper {
        padding: 0;
        border-radius: 5px !important;
        border: none !important;
        box-shadow: none !important;
        background-color: transparent;
        height: v-bind(height);
      }

      &__inner {
        font-family: $fontFamily !important;
        font-size: 12px;
        line-height: 16px;
      }
    }

    //TODO: Verificar se é necessário o box-shadow está com um problema visual com esse estilo
    &--disabled {
      background-color: #f5f7fa !important;
      //box-shadow: 0 0 0 1px #e4e7ed inset !important;

      &:hover {
        background-color: #f5f7fa !important;
      }

      &:focus-within {
        border: 1px solid $input-border !important;
      }
    }

    &--error {
      border: 1px solid $input-border-error;

      & > .f360-input {
        &__icon {
          font-size: 10px !important;
          color: $input-icon-color-error;
        }
      }
    }

    &--full {
      width: 100%;
    }

    &:hover {
      background-color: #edf7ff;
    }

    &:focus-within {
      border: 1px solid $input-border-active;
    }
  }

  &__icon {
    font-size: 16px !important;
    color: $input-icon-color;
  }

  &__error {
    font-family: $fontFamily;
    font-weight: 300;
    font-size: 10px;
    line-height: 12px;
    color: $input-icon-color-error;
  }

  &--full {
    width: 100%;
  }

  &[type="textarea"] {
    .f360-input__wrapper {
      padding: 0;

      .financas-textarea__inner {
        font-family: $fontFamily !important;
        font-size: 12px;
        line-height: 16px;
        background: transparent;
        box-shadow: inherit;
        padding: 6px v-bind(padding);
      }
    }
  }
}

.f360-baseform {
  .financas-form-item.is-error .f360-input .f360-input__wrapper,
  .financas-form-item.is-error .financas-select {
    border-color: #ff3033;
    position: relative;

    &::after {
      content: "*";
      right: 7px;
      top: 1px;
      color: #ff3033;
      position: absolute;
      font-weight: 600;
    }
  }
}
</style>
